/**
 *  このファイルはtools/generate.tsにより生成されたファイルです。手動で編集しないでください。
 */

import type { ExtendedDB } from "../../../types/generated/extendedDBCore";
import { z } from "zod";
import { GroupBaseSchema } from "../../../schema/generated/Group";
import { MaterialBaseSchema } from "../../../schema/generated/Material";
import { IssuanceBaseSchema } from "../../../schema/generated/Issuance";
import { CarryOutBaseSchema } from "../../../schema/generated/CarryOut";
import { LotBaseSchema } from "../../../schema/generated/Lot";
import { CarryInBaseSchema } from "../../../schema/generated/CarryIn";
import { ConstructionPlanBaseSchema } from "../../../schema/generated/ConstructionPlan";
import { ConstructionPlanAreaBaseSchema } from "../../../schema/generated/ConstructionPlanArea";
import { ConstructionBaseSchema } from "../../../schema/generated/Construction";
import { ConstructionRecordBaseSchema } from "../../../schema/generated/ConstructionRecord";
import { AreaDivisionBaseSchema } from "../../../schema/generated/AreaDivision";
import { MaterialDivisionBaseSchema } from "../../../schema/generated/MaterialDivision";
import { ClientBaseSchema } from "../../../schema/generated/Client";
import { WorkBaseSchema } from "../../../schema/generated/Work";
import { WorkTypeBaseSchema } from "../../../schema/generated/WorkType";
import { ConstructionMethodBaseSchema } from "../../../schema/generated/ConstructionMethod";
import { StirringMethodBaseSchema } from "../../../schema/generated/StirringMethod";
import { MaterialMasterBaseSchema } from "../../../schema/generated/MaterialMaster";
import { CarryInLocationBaseSchema } from "../../../schema/generated/CarryInLocation";
import { LocationDivisionBaseSchema } from "../../../schema/generated/LocationDivision";
import { WorkSubtypeBaseSchema } from "../../../schema/generated/WorkSubtype";
import { MaterialDetailBaseSchema } from "../../../schema/generated/MaterialDetail";
import { PotLifeSpecBaseSchema } from "../../../schema/generated/PotLifeSpec";
import { QuantityPlanBaseSchema } from "../../../schema/generated/QuantityPlan";
import { StockOrderBaseSchema } from "../../../schema/generated/StockOrder";
import { ConstructionPlanAreaLocationBaseSchema } from "../../../schema/generated/ConstructionPlanAreaLocation";
import { ConstructionPlanAreaPlannedMaterialBaseSchema } from "../../../schema/generated/ConstructionPlanAreaPlannedMaterial";
import { MixRatioDefinitionBaseSchema } from "../../../schema/generated/MixRatioDefinition";
import { WorkDetailBaseSchema } from "../../../schema/generated/WorkDetail";
import { MixRatioDetailBaseSchema } from "../../../schema/generated/MixRatioDetail";
import { QualityMeasurementItemBaseSchema } from "../../../schema/generated/QualityMeasurementItem";
import { ProcessExecutionBaseSchema } from "../../../schema/generated/ProcessExecution";
import { StirBatchBaseSchema } from "../../../schema/generated/StirBatch";
import { ConstructionActionBaseSchema } from "../../../schema/generated/ConstructionAction";
import { QualityRecordBaseSchema } from "../../../schema/generated/QualityRecord";
import { ConstructionActionAreaBaseSchema } from "../../../schema/generated/ConstructionActionArea";
import { ConstructionMaterialConsumptionBaseSchema } from "../../../schema/generated/ConstructionMaterialConsumption";
import { WorkTechnicianBaseSchema } from "../../../schema/generated/WorkTechnician";
import { AreaDivisionLocationLabelBaseSchema } from "../../../schema/generated/AreaDivisionLocationLabel";
import { LocationDivisionParentPathBaseSchema } from "../../../schema/generated/LocationDivisionParentPath";

export type IDBEntityIndex = {
  // index()で参照するインデックス名
  name: string;
  // インデックス対象のkeyPath
  keyPath: string[];
};

export type IDBEntityDef = {
  // エンティティ名(単数形)
  name: string;
  // IndexedDBのオブジェクトストア名
  store: string;
  // SetCacheがAPIレスポンスからエンティティを収集する際に探索するキー
  collectKeys: string[];
  // 収集したデータの検証スキーマ(入力は parse 前の未検証値のため unknown)
  schema: z.ZodType<{ id: string | number }, z.ZodTypeDef, unknown>;
  // オフラインでの新規作成時にserverGenerate列へ設定する初期値
  putDefaults: { [column: string]: boolean | number | string | null };
  // preparer_user_id列を持つか
  hasPreparer: boolean;
  // 外部キー検索用のセカンダリインデックス([ownerId, fk]複合)
  indexes: IDBEntityIndex[];
};

export type MergeRequestPath =
  | "/merge-carry-outs"
  | "/merge-lots"
  | "/merge-carry-ins"
  | "/merge-construction-plans"
  | "/merge-construction-plan-areas"
  | "/merge-constructions"
  | "/merge-construction-records"
  | "/merge-issuances"
  | "/merge-materials"
  | "/merge-construction-plan-area-locations"
  | "/merge-construction-plan-area-planned-materials"
  | "/merge-quantity-plans"
  | "/merge-stock-orders"
  | "/merge-process-executions"
  | "/merge-stir-batches"
  | "/merge-construction-actions"
  | "/merge-construction-action-areas"
  | "/merge-quality-records"
  | "/merge-construction-material-consumptions";

export type IDBMergeDef = {
  // 対象のオブジェクトストア名
  store: string;
  // 送信先エンドポイント
  path: MergeRequestPath;
  // 送信ペイロードの検証スキーマ
  payloadSchema: z.ZodTypeAny;
};

export const idbEntityDefs = {
  Group: {
    name: "Group",
    store: "Groups",
    collectKeys: ["Group", "Groups"],
    schema: GroupBaseSchema.base,
    putDefaults: { deleted_at: null },
    hasPreparer: false,
    indexes: [],
  },
  Material: {
    name: "Material",
    store: "Materials",
    collectKeys: ["Material", "Materials"],
    schema: MaterialBaseSchema.base,
    putDefaults: { issuance_id: null },
    hasPreparer: false,
    indexes: [{ name: "issuance_id", keyPath: ["ownerId", "issuance_id"] }, { name: "lot_id", keyPath: ["ownerId", "lot_id"] }, { name: "carry_in_id", keyPath: ["ownerId", "carry_in_id"] }],
  },
  Issuance: {
    name: "Issuance",
    store: "Issuances",
    collectKeys: ["Issuance", "Issuances"],
    schema: IssuanceBaseSchema.base,
    putDefaults: { deleted_at: null, remarks: null },
    hasPreparer: true,
    indexes: [],
  },
  CarryOut: {
    name: "CarryOut",
    store: "CarryOuts",
    collectKeys: ["CarryOut", "CarryOuts"],
    schema: CarryOutBaseSchema.base,
    putDefaults: { deleted_at: null, attachment_location: null, attached_by_user_id: null, attached_at: null, remarks: null },
    hasPreparer: true,
    indexes: [],
  },
  Lot: {
    name: "Lot",
    store: "Lots",
    collectKeys: ["Lot", "Lots"],
    schema: LotBaseSchema.base,
    putDefaults: { deleted_at: null, shipped_at: null, material_id: null, lot_no: null, manufactured_at: null, planned_count: 0, remarks: null },
    hasPreparer: true,
    indexes: [{ name: "carry_out_id", keyPath: ["ownerId", "carry_out_id"] }, { name: "material_id", keyPath: ["ownerId", "material_id"] }],
  },
  CarryIn: {
    name: "CarryIn",
    store: "CarryIns",
    collectKeys: ["CarryIn", "CarryIns"],
    schema: CarryInBaseSchema.base,
    putDefaults: { carried_in_at: null, deleted_at: null, work_id: null, carry_in_location_id: null, received_at: null, temperature: null, humidity: null, receiver_name: null, planned_count: 0, remarks: null },
    hasPreparer: true,
    indexes: [{ name: "work_id", keyPath: ["ownerId", "work_id"] }, { name: "carry_in_location_id", keyPath: ["ownerId", "carry_in_location_id"] }],
  },
  ConstructionPlan: {
    name: "ConstructionPlan",
    store: "ConstructionPlans",
    collectKeys: ["ConstructionPlan", "ConstructionPlans"],
    schema: ConstructionPlanBaseSchema.base,
    putDefaults: { deleted_at: null },
    hasPreparer: true,
    indexes: [{ name: "work_id", keyPath: ["ownerId", "work_id"] }],
  },
  ConstructionPlanArea: {
    name: "ConstructionPlanArea",
    store: "ConstructionPlanAreas",
    collectKeys: ["ConstructionPlanArea", "ConstructionPlanAreas"],
    schema: ConstructionPlanAreaBaseSchema.base,
    putDefaults: { deleted_at: null, work_id: null, work_type_id: null, work_subtype_id: null, planned_qty: null, planned_start: null, planned_end: null, remarks: null },
    hasPreparer: false,
    indexes: [{ name: "construction_plan_id", keyPath: ["ownerId", "construction_plan_id"] }, { name: "work_id", keyPath: ["ownerId", "work_id"] }, { name: "work_type_id", keyPath: ["ownerId", "work_type_id"] }, { name: "work_subtype_id", keyPath: ["ownerId", "work_subtype_id"] }],
  },
  Construction: {
    name: "Construction",
    store: "Constructions",
    collectKeys: ["Construction", "Constructions"],
    schema: ConstructionBaseSchema.base,
    putDefaults: { deleted_at: null, executed_at: null, ambient_temperature: null, ambient_humidity: null, finished_at: null, remarks: null, has_stir_execution: false },
    hasPreparer: true,
    indexes: [{ name: "construction_plan_id", keyPath: ["ownerId", "construction_plan_id"] }],
  },
  ConstructionRecord: {
    name: "ConstructionRecord",
    store: "ConstructionRecords",
    collectKeys: ["ConstructionRecord", "ConstructionRecords"],
    schema: ConstructionRecordBaseSchema.base,
    putDefaults: { deleted_at: null },
    hasPreparer: true,
    indexes: [{ name: "construction_id", keyPath: ["ownerId", "construction_id"] }],
  },
  AreaDivision: {
    name: "AreaDivision",
    store: "AreaDivisions",
    collectKeys: ["AreaDivision", "AreaDivisions"],
    schema: AreaDivisionBaseSchema.base,
    putDefaults: { deleted_at: null },
    hasPreparer: false,
    indexes: [],
  },
  MaterialDivision: {
    name: "MaterialDivision",
    store: "MaterialDivisions",
    collectKeys: ["MaterialDivision", "MaterialDivisions"],
    schema: MaterialDivisionBaseSchema.base,
    putDefaults: { deleted_at: null },
    hasPreparer: false,
    indexes: [],
  },
  Client: {
    name: "Client",
    store: "Clients",
    collectKeys: ["Client", "Clients"],
    schema: ClientBaseSchema.base,
    putDefaults: { deleted_at: null },
    hasPreparer: false,
    indexes: [],
  },
  Work: {
    name: "Work",
    store: "Works",
    collectKeys: ["Work", "Works"],
    schema: WorkBaseSchema.base,
    putDefaults: { deleted_at: null },
    hasPreparer: false,
    indexes: [{ name: "area_division_id", keyPath: ["ownerId", "area_division_id"] }, { name: "client_id", keyPath: ["ownerId", "client_id"] }],
  },
  WorkType: {
    name: "WorkType",
    store: "WorkTypes",
    collectKeys: ["WorkType", "WorkTypes"],
    schema: WorkTypeBaseSchema.base,
    putDefaults: { deleted_at: null },
    hasPreparer: false,
    indexes: [{ name: "area_division_id", keyPath: ["ownerId", "area_division_id"] }],
  },
  ConstructionMethod: {
    name: "ConstructionMethod",
    store: "ConstructionMethods",
    collectKeys: ["ConstructionMethod", "ConstructionMethods"],
    schema: ConstructionMethodBaseSchema.base,
    putDefaults: { deleted_at: null },
    hasPreparer: false,
    indexes: [{ name: "material_division_id", keyPath: ["ownerId", "material_division_id"] }],
  },
  StirringMethod: {
    name: "StirringMethod",
    store: "StirringMethods",
    collectKeys: ["StirringMethod", "StirringMethods"],
    schema: StirringMethodBaseSchema.base,
    putDefaults: { deleted_at: null },
    hasPreparer: false,
    indexes: [{ name: "material_division_id", keyPath: ["ownerId", "material_division_id"] }],
  },
  MaterialMaster: {
    name: "MaterialMaster",
    store: "MaterialMasters",
    collectKeys: ["MaterialMaster", "MaterialMasters"],
    schema: MaterialMasterBaseSchema.base,
    putDefaults: { deleted_at: null },
    hasPreparer: false,
    indexes: [{ name: "material_division_id", keyPath: ["ownerId", "material_division_id"] }],
  },
  CarryInLocation: {
    name: "CarryInLocation",
    store: "CarryInLocations",
    collectKeys: ["CarryInLocation", "CarryInLocations"],
    schema: CarryInLocationBaseSchema.base,
    putDefaults: { deleted_at: null },
    hasPreparer: false,
    indexes: [{ name: "area_division_id", keyPath: ["ownerId", "area_division_id"] }],
  },
  LocationDivision: {
    name: "LocationDivision",
    store: "LocationDivisions",
    collectKeys: ["LocationDivision", "LocationDivisions"],
    schema: LocationDivisionBaseSchema.base,
    putDefaults: { deleted_at: null },
    hasPreparer: false,
    indexes: [{ name: "area_division_id", keyPath: ["ownerId", "area_division_id"] }],
  },
  WorkSubtype: {
    name: "WorkSubtype",
    store: "WorkSubtypes",
    collectKeys: ["WorkSubtype", "WorkSubtypes"],
    schema: WorkSubtypeBaseSchema.base,
    putDefaults: { deleted_at: null },
    hasPreparer: false,
    indexes: [{ name: "area_division_id", keyPath: ["ownerId", "area_division_id"] }, { name: "work_type_id", keyPath: ["ownerId", "work_type_id"] }],
  },
  MaterialDetail: {
    name: "MaterialDetail",
    store: "MaterialDetails",
    collectKeys: ["MaterialDetail", "MaterialDetails"],
    schema: MaterialDetailBaseSchema.base,
    putDefaults: { deleted_at: null },
    hasPreparer: false,
    indexes: [{ name: "material_division_id", keyPath: ["ownerId", "material_division_id"] }, { name: "material_id", keyPath: ["ownerId", "material_id"] }],
  },
  PotLifeSpec: {
    name: "PotLifeSpec",
    store: "PotLifeSpecs",
    collectKeys: ["PotLifeSpec", "PotLifeSpecs"],
    schema: PotLifeSpecBaseSchema.base,
    putDefaults: { deleted_at: null },
    hasPreparer: false,
    indexes: [{ name: "material_division_id", keyPath: ["ownerId", "material_division_id"] }, { name: "material_id", keyPath: ["ownerId", "material_id"] }],
  },
  QuantityPlan: {
    name: "QuantityPlan",
    store: "QuantityPlans",
    collectKeys: ["QuantityPlan", "QuantityPlans"],
    schema: QuantityPlanBaseSchema.base,
    putDefaults: { location_slot1: "", location_slot2: "", planned_qty: 0, unit: "", deleted_at: null },
    hasPreparer: false,
    indexes: [{ name: "work_id", keyPath: ["ownerId", "work_id"] }, { name: "work_type_id", keyPath: ["ownerId", "work_type_id"] }, { name: "work_subtype_id", keyPath: ["ownerId", "work_subtype_id"] }],
  },
  StockOrder: {
    name: "StockOrder",
    store: "StockOrders",
    collectKeys: ["StockOrder", "StockOrders"],
    schema: StockOrderBaseSchema.base,
    putDefaults: { order_kind: "", qty: 0, unit: "", deleted_at: null },
    hasPreparer: false,
    indexes: [{ name: "work_id", keyPath: ["ownerId", "work_id"] }, { name: "material_id", keyPath: ["ownerId", "material_id"] }],
  },
  ConstructionPlanAreaLocation: {
    name: "ConstructionPlanAreaLocation",
    store: "ConstructionPlanAreaLocations",
    collectKeys: ["ConstructionPlanAreaLocation", "ConstructionPlanAreaLocations"],
    schema: ConstructionPlanAreaLocationBaseSchema.base,
    putDefaults: { slot: 0, deleted_at: null },
    hasPreparer: false,
    indexes: [{ name: "construction_plan_area_id", keyPath: ["ownerId", "construction_plan_area_id"] }, { name: "location_division_id", keyPath: ["ownerId", "location_division_id"] }],
  },
  ConstructionPlanAreaPlannedMaterial: {
    name: "ConstructionPlanAreaPlannedMaterial",
    store: "ConstructionPlanAreaPlannedMaterials",
    collectKeys: ["ConstructionPlanAreaPlannedMaterial", "ConstructionPlanAreaPlannedMaterials"],
    schema: ConstructionPlanAreaPlannedMaterialBaseSchema.base,
    putDefaults: { work_detail_id: null, planned_qty: 0, unit: "", deleted_at: null },
    hasPreparer: false,
    indexes: [{ name: "construction_plan_area_id", keyPath: ["ownerId", "construction_plan_area_id"] }, { name: "material_id", keyPath: ["ownerId", "material_id"] }, { name: "work_detail_id", keyPath: ["ownerId", "work_detail_id"] }],
  },
  MixRatioDefinition: {
    name: "MixRatioDefinition",
    store: "MixRatioDefinitions",
    collectKeys: ["MixRatioDefinition", "MixRatioDefinitions"],
    schema: MixRatioDefinitionBaseSchema.base,
    putDefaults: { deleted_at: null },
    hasPreparer: false,
    indexes: [{ name: "material_division_id", keyPath: ["ownerId", "material_division_id"] }, { name: "material_id", keyPath: ["ownerId", "material_id"] }, { name: "stirring_method_id", keyPath: ["ownerId", "stirring_method_id"] }, { name: "construction_method_id", keyPath: ["ownerId", "construction_method_id"] }],
  },
  WorkDetail: {
    name: "WorkDetail",
    store: "WorkDetails",
    collectKeys: ["WorkDetail", "WorkDetails"],
    schema: WorkDetailBaseSchema.base,
    putDefaults: { deleted_at: null },
    hasPreparer: false,
    indexes: [{ name: "area_division_id", keyPath: ["ownerId", "area_division_id"] }, { name: "work_subtype_id", keyPath: ["ownerId", "work_subtype_id"] }, { name: "mix_ratio_definition_id", keyPath: ["ownerId", "mix_ratio_definition_id"] }],
  },
  MixRatioDetail: {
    name: "MixRatioDetail",
    store: "MixRatioDetails",
    collectKeys: ["MixRatioDetail", "MixRatioDetails"],
    schema: MixRatioDetailBaseSchema.base,
    putDefaults: { deleted_at: null },
    hasPreparer: false,
    indexes: [{ name: "material_division_id", keyPath: ["ownerId", "material_division_id"] }, { name: "material_id", keyPath: ["ownerId", "material_id"] }, { name: "mix_ratio_definition_id", keyPath: ["ownerId", "mix_ratio_definition_id"] }, { name: "material_detail_id", keyPath: ["ownerId", "material_detail_id"] }],
  },
  QualityMeasurementItem: {
    name: "QualityMeasurementItem",
    store: "QualityMeasurementItems",
    collectKeys: ["QualityMeasurementItem", "QualityMeasurementItems"],
    schema: QualityMeasurementItemBaseSchema.base,
    putDefaults: { deleted_at: null },
    hasPreparer: false,
    indexes: [{ name: "area_division_id", keyPath: ["ownerId", "area_division_id"] }, { name: "material_id", keyPath: ["ownerId", "material_id"] }, { name: "work_type_id", keyPath: ["ownerId", "work_type_id"] }, { name: "work_subtype_id", keyPath: ["ownerId", "work_subtype_id"] }, { name: "work_detail_id", keyPath: ["ownerId", "work_detail_id"] }],
  },
  ProcessExecution: {
    name: "ProcessExecution",
    store: "ProcessExecutions",
    collectKeys: ["ProcessExecution", "ProcessExecutions"],
    schema: ProcessExecutionBaseSchema.base,
    putDefaults: { deleted_at: null },
    hasPreparer: false,
    indexes: [{ name: "construction_id", keyPath: ["ownerId", "construction_id"] }, { name: "construction_plan_area_id", keyPath: ["ownerId", "construction_plan_area_id"] }, { name: "work_detail_id", keyPath: ["ownerId", "work_detail_id"] }],
  },
  StirBatch: {
    name: "StirBatch",
    store: "StirBatchs",
    collectKeys: ["StirBatch", "StirBatchs"],
    schema: StirBatchBaseSchema.base,
    putDefaults: { sort_order: 0, deleted_at: null },
    hasPreparer: false,
    indexes: [{ name: "construction_id", keyPath: ["ownerId", "construction_id"] }, { name: "process_execution_id", keyPath: ["ownerId", "process_execution_id"] }, { name: "work_detail_id", keyPath: ["ownerId", "work_detail_id"] }, { name: "mix_ratio_definition_id", keyPath: ["ownerId", "mix_ratio_definition_id"] }],
  },
  ConstructionAction: {
    name: "ConstructionAction",
    store: "ConstructionActions",
    collectKeys: ["ConstructionAction", "ConstructionActions"],
    schema: ConstructionActionBaseSchema.base,
    putDefaults: { sort_order: 0, deleted_at: null },
    hasPreparer: false,
    indexes: [{ name: "construction_id", keyPath: ["ownerId", "construction_id"] }, { name: "process_execution_id", keyPath: ["ownerId", "process_execution_id"] }, { name: "work_detail_id", keyPath: ["ownerId", "work_detail_id"] }],
  },
  QualityRecord: {
    name: "QualityRecord",
    store: "QualityRecords",
    collectKeys: ["QualityRecord", "QualityRecords"],
    schema: QualityRecordBaseSchema.base,
    putDefaults: { deleted_at: null },
    hasPreparer: false,
    indexes: [{ name: "construction_id", keyPath: ["ownerId", "construction_id"] }, { name: "construction_plan_area_id", keyPath: ["ownerId", "construction_plan_area_id"] }, { name: "quality_measurement_item_id", keyPath: ["ownerId", "quality_measurement_item_id"] }, { name: "construction_action_id", keyPath: ["ownerId", "construction_action_id"] }],
  },
  ConstructionActionArea: {
    name: "ConstructionActionArea",
    store: "ConstructionActionAreas",
    collectKeys: ["ConstructionActionArea", "ConstructionActionAreas"],
    schema: ConstructionActionAreaBaseSchema.base,
    putDefaults: { deleted_at: null },
    hasPreparer: false,
    indexes: [{ name: "construction_action_id", keyPath: ["ownerId", "construction_action_id"] }, { name: "construction_plan_area_id", keyPath: ["ownerId", "construction_plan_area_id"] }],
  },
  ConstructionMaterialConsumption: {
    name: "ConstructionMaterialConsumption",
    store: "ConstructionMaterialConsumptions",
    collectKeys: ["ConstructionMaterialConsumption", "ConstructionMaterialConsumptions"],
    schema: ConstructionMaterialConsumptionBaseSchema.base,
    putDefaults: { used_ratio: 1, deleted_at: null },
    hasPreparer: false,
    indexes: [{ name: "construction_id", keyPath: ["ownerId", "construction_id"] }, { name: "material_qr_id", keyPath: ["ownerId", "material_qr_id"] }, { name: "stir_batch_id", keyPath: ["ownerId", "stir_batch_id"] }, { name: "process_execution_id", keyPath: ["ownerId", "process_execution_id"] }],
  },
  WorkTechnician: {
    name: "WorkTechnician",
    store: "WorkTechnicians",
    collectKeys: ["WorkTechnician", "WorkTechnicians"],
    schema: WorkTechnicianBaseSchema.base,
    putDefaults: { name: "", affiliation: "", role: "", sort_order: 0, deleted_at: null },
    hasPreparer: false,
    indexes: [{ name: "work_id", keyPath: ["ownerId", "work_id"] }],
  },
  AreaDivisionLocationLabel: {
    name: "AreaDivisionLocationLabel",
    store: "AreaDivisionLocationLabels",
    collectKeys: ["AreaDivisionLocationLabel", "AreaDivisionLocationLabels"],
    schema: AreaDivisionLocationLabelBaseSchema.base,
    putDefaults: { slot: 0, label_name: "", is_required: false, deleted_at: null },
    hasPreparer: false,
    indexes: [{ name: "area_division_id", keyPath: ["ownerId", "area_division_id"] }],
  },
  LocationDivisionParentPath: {
    name: "LocationDivisionParentPath",
    store: "LocationDivisionParentPaths",
    collectKeys: ["LocationDivisionParentPath", "LocationDivisionParentPaths"],
    schema: LocationDivisionParentPathBaseSchema.base,
    putDefaults: { ancestor_slot: 0, ancestor_division_name: "", deleted_at: null },
    hasPreparer: false,
    indexes: [{ name: "location_division_id", keyPath: ["ownerId", "location_division_id"] }],
  },
} satisfies { [name: string]: IDBEntityDef };

// SetCache/RemoveInActiveCacheが走査する全エンティティ定義
export const idbEntityDefList: IDBEntityDef[] = Object.values(idbEntityDefs);

// オンライン復帰時のマージ送信定義(親→子の依存順)
export const idbMergeDefs: IDBMergeDef[] = [
  { store: "CarryOuts", path: "/merge-carry-outs", payloadSchema: CarryOutBaseSchema.caches },
  { store: "Lots", path: "/merge-lots", payloadSchema: LotBaseSchema.caches },
  { store: "CarryIns", path: "/merge-carry-ins", payloadSchema: CarryInBaseSchema.caches },
  { store: "ConstructionPlans", path: "/merge-construction-plans", payloadSchema: ConstructionPlanBaseSchema.caches },
  { store: "ConstructionPlanAreas", path: "/merge-construction-plan-areas", payloadSchema: ConstructionPlanAreaBaseSchema.caches },
  { store: "Constructions", path: "/merge-constructions", payloadSchema: ConstructionBaseSchema.caches },
  { store: "ConstructionRecords", path: "/merge-construction-records", payloadSchema: ConstructionRecordBaseSchema.caches },
  { store: "Issuances", path: "/merge-issuances", payloadSchema: IssuanceBaseSchema.caches },
  { store: "Materials", path: "/merge-materials", payloadSchema: MaterialBaseSchema.caches },
  { store: "ConstructionPlanAreaLocations", path: "/merge-construction-plan-area-locations", payloadSchema: ConstructionPlanAreaLocationBaseSchema.caches },
  { store: "ConstructionPlanAreaPlannedMaterials", path: "/merge-construction-plan-area-planned-materials", payloadSchema: ConstructionPlanAreaPlannedMaterialBaseSchema.caches },
  { store: "QuantityPlans", path: "/merge-quantity-plans", payloadSchema: QuantityPlanBaseSchema.caches },
  { store: "StockOrders", path: "/merge-stock-orders", payloadSchema: StockOrderBaseSchema.caches },
  { store: "ProcessExecutions", path: "/merge-process-executions", payloadSchema: ProcessExecutionBaseSchema.caches },
  { store: "StirBatchs", path: "/merge-stir-batches", payloadSchema: StirBatchBaseSchema.caches },
  { store: "ConstructionActions", path: "/merge-construction-actions", payloadSchema: ConstructionActionBaseSchema.caches },
  { store: "ConstructionActionAreas", path: "/merge-construction-action-areas", payloadSchema: ConstructionActionAreaBaseSchema.caches },
  { store: "QualityRecords", path: "/merge-quality-records", payloadSchema: QualityRecordBaseSchema.caches },
  { store: "ConstructionMaterialConsumptions", path: "/merge-construction-material-consumptions", payloadSchema: ConstructionMaterialConsumptionBaseSchema.caches },
];

export type EntityGetPlural =
  | "area-divisions"
  | "material-divisions"
  | "clients"
  | "works"
  | "work-types"
  | "construction-methods"
  | "stirring-methods"
  | "material-masters"
  | "carry-in-locations"
  | "location-divisions"
  | "work-subtypes"
  | "material-details"
  | "pot-life-specs"
  | "quantity-plans"
  | "stock-orders"
  | "construction-plan-area-locations"
  | "construction-plan-area-planned-materials"
  | "mix-ratio-definitions"
  | "work-details"
  | "mix-ratio-details"
  | "quality-measurement-items"
  | "process-executions"
  | "stir-batches"
  | "construction-actions"
  | "quality-records"
  | "construction-action-areas"
  | "construction-material-consumptions"
  | "work-technicians"
  | "area-division-location-labels"
  | "location-division-parent-paths"
  | "materials"
  | "issuances"
  | "carry-outs"
  | "lots"
  | "carry-ins"
  | "construction-plans"
  | "construction-plan-areas"
  | "constructions"
  | "construction-records";

export type EntityGetPath = `/get-${EntityGetPlural}`;

export type EntitySlugInfo = {
  sing: string;       // 単数形 kebab-case(URL用)
  plural: EntityGetPlural; // 複数形 kebab-case(URL用)
  idType: "int" | "uuid";
  hasMerge: boolean;
  nestedLabels?: true; // AreaDivision の put/patch で location_labels[] を同送する
};

export const entitySlugMap = {
  areaDivisions: { sing: "area-division", plural: "area-divisions", idType: "int" as const, hasMerge: false, nestedLabels: true as const },
  materialDivisions: { sing: "material-division", plural: "material-divisions", idType: "int" as const, hasMerge: false },
  clients: { sing: "client", plural: "clients", idType: "int" as const, hasMerge: false },
  works: { sing: "work", plural: "works", idType: "int" as const, hasMerge: false },
  workTypes: { sing: "work-type", plural: "work-types", idType: "int" as const, hasMerge: false },
  constructionMethods: { sing: "construction-method", plural: "construction-methods", idType: "int" as const, hasMerge: false },
  stirringMethods: { sing: "stirring-method", plural: "stirring-methods", idType: "int" as const, hasMerge: false },
  materials: { sing: "material-master", plural: "material-masters", idType: "int" as const, hasMerge: false },
  carryInLocations: { sing: "carry-in-location", plural: "carry-in-locations", idType: "int" as const, hasMerge: false },
  locationDivisions: { sing: "location-division", plural: "location-divisions", idType: "int" as const, hasMerge: false },
  workSubtypes: { sing: "work-subtype", plural: "work-subtypes", idType: "int" as const, hasMerge: false },
  materialDetails: { sing: "material-detail", plural: "material-details", idType: "int" as const, hasMerge: false },
  potLifeSpecs: { sing: "pot-life-spec", plural: "pot-life-specs", idType: "int" as const, hasMerge: false },
  quantityPlans: { sing: "quantity-plan", plural: "quantity-plans", idType: "uuid" as const, hasMerge: true },
  stockOrders: { sing: "stock-order", plural: "stock-orders", idType: "uuid" as const, hasMerge: true },
  constructionPlanAreaLocations: { sing: "construction-plan-area-location", plural: "construction-plan-area-locations", idType: "uuid" as const, hasMerge: true },
  constructionPlanAreaPlannedMaterials: { sing: "construction-plan-area-planned-material", plural: "construction-plan-area-planned-materials", idType: "uuid" as const, hasMerge: true },
  mixRatioDefinitions: { sing: "mix-ratio-definition", plural: "mix-ratio-definitions", idType: "int" as const, hasMerge: false },
  workDetails: { sing: "work-detail", plural: "work-details", idType: "int" as const, hasMerge: false },
  mixRatioDetails: { sing: "mix-ratio-detail", plural: "mix-ratio-details", idType: "int" as const, hasMerge: false },
  qualityMeasurementItems: { sing: "quality-measurement-item", plural: "quality-measurement-items", idType: "int" as const, hasMerge: false },
  processExecutions: { sing: "process-execution", plural: "process-executions", idType: "uuid" as const, hasMerge: true },
  stirBatches: { sing: "stir-batch", plural: "stir-batches", idType: "uuid" as const, hasMerge: true },
  constructionActions: { sing: "construction-action", plural: "construction-actions", idType: "uuid" as const, hasMerge: true },
  qualityRecords: { sing: "quality-record", plural: "quality-records", idType: "uuid" as const, hasMerge: true },
  constructionActionAreas: { sing: "construction-action-area", plural: "construction-action-areas", idType: "uuid" as const, hasMerge: true },
  constructionMaterialConsumptions: { sing: "construction-material-consumption", plural: "construction-material-consumptions", idType: "uuid" as const, hasMerge: true },
  workTechnicians: { sing: "work-technician", plural: "work-technicians", idType: "int" as const, hasMerge: false },
  areaDivisionLocationLabels: { sing: "area-division-location-label", plural: "area-division-location-labels", idType: "int" as const, hasMerge: false },
  locationDivisionParentPaths: { sing: "location-division-parent-path", plural: "location-division-parent-paths", idType: "int" as const, hasMerge: false },
} satisfies Partial<Record<keyof ExtendedDB, EntitySlugInfo>>;